import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, LogOut, Shield, Users, FileText, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

export default function Admin() {
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [authed, setAuthed] = useState(false);
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("users");
  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        await api.get("/admin/me");
        setAuthed(true);
      } catch (_e) {
        setAuthed(false);
      } finally {
        setChecking(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!authed) return;
    (async () => {
      try {
        const { data } = await api.get("/admin/stats");
        setStats(data);
      } catch (_e) {}
    })();
  }, [authed]);

  useEffect(() => {
    if (!authed) return;
    setRows(null);
    (async () => {
      try {
        const { data } = await api.get(`/admin/${tab}`);
        setRows(data);
      } catch (e) {
        toast.error(formatApiError(e));
        setRows([]);
      }
    })();
  }, [authed, tab]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/admin/login", { password });
      setAuthed(true);
      toast.success("Welcome, admin.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    await api.post("/admin/logout").catch(() => {});
    setAuthed(false);
    setPassword("");
    navigate("/", { replace: true });
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-stone-950 grid place-items-center text-amber-200/70 font-mono-mini text-xs uppercase tracking-widest">
        Checking session…
      </div>
    );
  }

  if (!authed) {
    return (
      <div className="min-h-screen bg-stone-950 text-stone-100 grid place-items-center p-6">
        <form onSubmit={submit} className="w-full max-w-sm rounded-3xl border border-amber-900/40 bg-stone-900/70 p-8">
          <div className="flex items-center gap-2 mb-6">
            <Shield className="w-5 h-5 text-amber-400" />
            <h1 className="font-serif-editorial text-2xl">Admin console</h1>
          </div>
          <label className="font-mono-mini text-[10px] uppercase tracking-[0.28em] text-amber-200/70 block mb-2">
            Password
          </label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="admin-password-input"
            className="w-full bg-stone-950/70 border border-stone-800 focus:border-amber-500 outline-none rounded-xl px-4 py-3 text-stone-100"
            placeholder="Admin password"
            autoFocus
          />
          <button
            type="submit"
            disabled={loading || !password}
            data-testid="admin-login-btn"
            className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-amber-500 hover:bg-amber-400 disabled:opacity-60 text-stone-950 font-semibold py-3 transition-all"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Enter
          </button>
        </form>
      </div>
    );
  }

  const TABS = [
    { id: "users", label: "Users", icon: Users, count: stats?.users },
    { id: "drafts", label: "Drafts", icon: FileText, count: stats?.drafts },
    { id: "messages", label: "Messages", icon: MessageSquare, count: stats?.messages },
  ];

  return (
    <div className="min-h-screen bg-stone-950 text-stone-100">
      <header className="border-b border-stone-900 bg-stone-950/90 backdrop-blur-xl sticky top-0 z-10">
        <div className="max-w-6xl mx-auto flex items-center justify-between px-6 py-4">
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-amber-400" />
            <span className="font-serif-editorial text-2xl">Admin · Kuch Kahna Hai</span>
          </div>
          <button
            onClick={logout}
            data-testid="admin-logout-btn"
            className="inline-flex items-center gap-2 rounded-full border border-stone-800 hover:border-rose-500/60 text-stone-300 hover:text-rose-200 px-3 py-1.5 text-sm transition-all"
          >
            <LogOut className="w-4 h-4" /> Log out
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto p-6">
        <div className="flex gap-2 mb-6 flex-wrap">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`admin-tab-${t.id}`}
              className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm transition-all ${
                tab === t.id
                  ? "bg-amber-500 text-stone-950 font-semibold"
                  : "bg-stone-900 border border-stone-800 text-stone-300 hover:border-amber-500/50"
              }`}
            >
              <t.icon className="w-4 h-4" />
              {t.label}
              <span className="font-mono-mini text-[10px] opacity-80">{t.count ?? "—"}</span>
            </button>
          ))}
        </div>

        <div className="rounded-2xl border border-stone-800 bg-stone-900/50 overflow-hidden">
          {rows === null && (
            <div className="p-8 text-center text-stone-500 flex items-center justify-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          )}
          {rows && rows.length === 0 && (
            <div className="p-8 text-center text-stone-500">No records.</div>
          )}
          {rows && rows.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid={`admin-table-${tab}`}>
                <thead className="bg-stone-900 text-stone-400 text-[10px] uppercase tracking-widest">
                  <tr>
                    {Object.keys(rows[0]).map((k) => (
                      <th key={k} className="text-left px-4 py-3 font-medium">{k}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={r.id || i} className="border-t border-stone-800 hover:bg-stone-900/60">
                      {Object.keys(rows[0]).map((k) => (
                        <td key={k} className="px-4 py-3 text-stone-200 align-top max-w-[420px]">
                          <div className="truncate" title={String(r[k] ?? "")}>
                            {typeof r[k] === "string" && r[k].length > 200 ? r[k].slice(0, 200) + "…" : String(r[k] ?? "")}
                          </div>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
